import { z } from 'zod'
import { createTracking } from '@mc/tracking-kit'
import { BRAND_NAME } from '../constants'

const ANALYTICS_PRODUCT_NAME = 'jarvis'

export const jarvisPageNames = {
  WELCOME: `${BRAND_NAME} announcement page (pre-login)`,
  LOGIN: `${BRAND_NAME} Login (pre-login)`,
  MODULE_DIALOG: `${BRAND_NAME} Module Dialog (post-login)`,
} as const

const pageSource = z.enum([
  jarvisPageNames.WELCOME,
  jarvisPageNames.LOGIN,
  jarvisPageNames.MODULE_DIALOG,
])

const baseSchema = z.object({
  product: z.literal(ANALYTICS_PRODUCT_NAME),
  page: pageSource,
})

const moduleSchema = z.object({
  subject_slug: z.string(),
  module_slug: z.string(),
  topic_slug: z.string().optional(),
})

const commonSchema = baseSchema.and(
  z.discriminatedUnion('page', [
    z.object({
      page: pageSource.extract([jarvisPageNames.WELCOME]),
    }),
    z.object({
      page: pageSource.extract([jarvisPageNames.LOGIN]),
    }),
    z.object({
      page: pageSource.extract([jarvisPageNames.MODULE_DIALOG]),
      ...moduleSchema.shape,
    }),
  ]),
)

const locations = z.enum([
  // POC
  'login-form',
  'sidebar',
  'sidebar - module selector',
  'sidebar - user menu',
  'chat',
  'history',
  // Announcement
  'email form',
  'user info form',
  'hero',
  'hero navbar',
])

const messageRoles = z.enum(['user', 'bot'])

const jarvisSchema = {
  jarvisClick: {
    eventName: `${BRAND_NAME} Click`,
    schema: commonSchema.and(
      z.object({
        action: z.string(),
        location: locations,
        selected_module_slug: z.string().optional(),
      }),
    ),
  },
  jarvisSignClick: {
    eventName: `${BRAND_NAME} Sign Click`,
    schema: commonSchema.and(
      z.object({
        action: z.string(),
        location: locations,
        selected_module_slug: z.string().optional(),
      }),
    ),
  },
  jarvisViewed: {
    eventName: `${BRAND_NAME} Viewed`,
    schema: commonSchema.and(
      z.object({
        name: z.string(),
      }),
    ),
  },
  jarvisSignViewed: {
    eventName: `${BRAND_NAME} Sign Viewed`,
    schema: commonSchema.and(
      z.object({
        name: z.string(),
      }),
    ),
  },
  jarvisRowViewed: {
    eventName: `${BRAND_NAME} Row Viewed`,
    schema: commonSchema.and(
      z.object({
        row_name: z.string(),
        row_position_index: z.number(),
      }),
    ),
  },
  jarvisPastMessagesScrolled: {
    eventName: `${BRAND_NAME} Past Messages Scrolled`,
    schema: commonSchema.and(
      z.object({
        location: z.literal('history'),
      }),
    ),
  },
  jarvisInteractionStarted: {
    eventName: `${BRAND_NAME} Interaction Started`,
    schema: commonSchema.and(
      z.object({
        interaction_id: z.string().uuid(),
      }),
    ),
  },
  jarvisInteractionFirstContent: {
    eventName: `${BRAND_NAME} Interaction First Content`,
    schema: commonSchema.and(
      z.object({
        interaction_id: z.string().uuid(),
      }),
    ),
  },
  jarvisInteraction: {
    eventName: `${BRAND_NAME} Interaction`,
    schema: commonSchema.and(
      z.object({
        message_uuid: z.string(),
        role: messageRoles,
        interaction_id: z.string().uuid(),
      }),
    ),
  },
  jarvisEmailCaptured: {
    eventName: `${BRAND_NAME} Email Captured`,
    schema: commonSchema.and(
      z.object({
        email: z.string().email(),
        location: z.literal('email form'),
      }),
    ),
  },
  jarvisUserInfoCaptured: {
    eventName: `${BRAND_NAME} User Info Captured`,
    schema: commonSchema.and(
      z.object({
        email: z.string().email(),
        location: z.literal('user info form'),
        professional_goals: z.array(z.string()),
        goal_other: z.string().optional(),
        years_experience: z.string(),
        current_role: z.string(),
        industry: z.string(),
        learning_timeline: z.string(),
      }),
    ),
  },
} as const

const pageSchema = z
  .object({
    name: pageSource,
    product: z.literal(ANALYTICS_PRODUCT_NAME),
    page: z.string().optional(),
    subject_slug: z.string().optional(),
    module_slug: z.string().optional(),
    topic_slug: z.string().optional(),
  })
  .passthrough()

const {
  useFireEvent,
  TrackExperimentViewed,
  TrackEvent,
  TrackClick,
  TrackPageViewed,
  TrackOnMount,
  TrackVisible,
  TrackingAttributes,
} = createTracking(jarvisSchema, pageSchema)

export {
  useFireEvent,
  TrackExperimentViewed,
  TrackEvent,
  TrackClick,
  TrackPageViewed,
  TrackOnMount,
  TrackVisible,
  TrackingAttributes,
}
